// Central catalog of tools (slug → title + short description). Plain data, no
// React imports, so it's safe to import anywhere — UI components, route
// handlers, and the OG-image generator. Source of truth for RelatedTools and
// the per-tool OG cards (app/api/og/tool/[slug]).

export interface ToolLink {
  slug: string;
  title: string;
  description: string;
  // Recently-shipped tools being tested/redesigned before public launch. Set on
  // catalog entries that must still resolve by slug (OG cards, share links,
  // /saved) but should disappear from discovery surfaces — sidebar nav +
  // search, RelatedTools cross-links, sitemap. See the 2026-07-23 hide pass.
  hidden?: boolean;
}

export const TOOLS: Record<string, ToolLink> = {
  'level-dat': {
    slug: 'level-dat',
    title: 'Level.dat Editor',
    description: 'Edit world settings, game rules, and spawn point.',
  },
  'singleplayer-data': {
    slug: 'singleplayer-data',
    title: 'Single Player Editor',
    description: 'Edit singleplayer inventory, stats, and position.',
  },
  'player-data': {
    slug: 'player-data',
    title: 'Server Player Editor',
    description: 'Edit server player data from UUID .dat files.',
  },
  'skin-editor': {
    slug: 'skin-editor',
    title: 'Skin Editor',
    description: 'Paint custom Minecraft skins with 3D preview.',
  },
  'enchantment-builder': {
    slug: 'enchantment-builder',
    title: 'Enchantment Builder',
    description: 'Create custom enchanted items with any levels.',
  },
  'anvil-calculator': {
    slug: 'anvil-calculator',
    title: 'Anvil Calculator',
    description: 'Cheapest order to combine enchanted books in an anvil.',
  },
  'uuid-lookup': {
    slug: 'uuid-lookup',
    title: 'UUID Lookup',
    description: 'Look up Minecraft UUIDs and download skins.',
  },
  'skin-stealer': {
    slug: 'skin-stealer',
    title: 'Skin Stealer',
    description: 'Grab & download any player\'s skin by username.',
  },
  'beacon-range-calculator': {
    slug: 'beacon-range-calculator',
    title: 'Beacon Range Calculator',
    description: 'Beacon range, pyramid size, effects and beam color.',
    hidden: true,
  },
  'pixel-circle-generator': {
    slug: 'pixel-circle-generator',
    title: 'Circle Generator',
    description: 'Block-perfect circles, spheres, pyramids, cylinders and more, with a 3D preview.',
    hidden: true,
  },
  'sphere-generator': {
    slug: 'sphere-generator',
    title: 'Sphere Generator',
    description: 'Block-perfect spheres and balls with a layer guide and 3D preview.',
    hidden: true,
  },
  'pyramid-generator': {
    slug: 'pyramid-generator',
    title: 'Pyramid Generator',
    description: 'Square pyramids from a base width, with block counts and 3D.',
    hidden: true,
  },
  'cylinder-generator': {
    slug: 'cylinder-generator',
    title: 'Cylinder Generator',
    description: 'Round towers and tubes, solid or hollow, with a 3D preview.',
    hidden: true,
  },
  'dome-generator': {
    slug: 'dome-generator',
    title: 'Dome Generator',
    description: 'Domes and hemisphere roofs, filled or hollow, with 3D.',
    hidden: true,
  },
  'cube-generator': {
    slug: 'cube-generator',
    title: 'Cube & Box Generator',
    description: 'Solid or hollow cubes and boxes with the exact block count.',
    hidden: true,
  },
  'diamond-generator': {
    slug: 'diamond-generator',
    title: 'Diamond Generator',
    description: 'A 3D diamond (octahedron) or flat diamond outline, with 3D.',
    hidden: true,
  },
  'motd-generator': {
    slug: 'motd-generator',
    title: 'MOTD Generator',
    description: 'Two-line colored server MOTD for server.properties.',
  },
  'server-status': {
    slug: 'server-status',
    title: 'Server Status Checker',
    description: 'Is a Java or Bedrock server online? Players, version, MOTD and icon.',
  },
  'nether-portal-calculator': {
    slug: 'nether-portal-calculator',
    title: 'Nether Portal Calculator',
    description: 'Convert Overworld ↔ Nether coordinates and check portal linking.',
  },
  'color-codes': {
    slug: 'color-codes',
    title: 'Color Codes',
    description: 'Every Minecraft color and formatting code, with hex values, click to copy.',
    hidden: true,
  },
  'colored-text-generator': {
    slug: 'colored-text-generator',
    title: 'Colored Text Generator',
    description: 'Style text with colors and copy it as §, &, MiniMessage or JSON.',
    hidden: true,
  },
  'gradient-generator': {
    slug: 'gradient-generator',
    title: 'Gradient Generator',
    description: 'Blend hex colors across text for any server or version.',
    hidden: true,
  },
  'title-generator': {
    slug: 'title-generator',
    title: 'Title Generator',
    description: 'Build /title, subtitle and actionbar commands with timings.',
  },
  'command-generator': {
    slug: 'command-generator',
    title: 'Command Generator',
    description: 'Build /give, /summon, /effect commands visually.',
  },
  'execute-builder': {
    slug: 'execute-builder',
    title: 'Execute Command Builder',
    description: 'Stack /execute subcommands visually.',
  },
  'loot-table-builder': {
    slug: 'loot-table-builder',
    title: 'Loot Table Builder',
    description: 'Design datapack loot tables for chests, mobs, blocks.',
  },
  'sulfur-cube-archetype-builder': {
    slug: 'sulfur-cube-archetype-builder',
    title: 'Sulfur Cube Archetype Builder',
    description: 'All 12 vanilla archetypes, plus a builder for custom ones.',
  },
  'recipe-viewer': {
    slug: 'recipe-viewer',
    title: 'Recipe Viewer',
    description: 'Look up crafting recipes for any item.',
  },
  'sign-generator': {
    slug: 'sign-generator',
    title: 'Sign Editor',
    description: 'Design custom signs with colors and click events.',
  },
  'tellraw-editor': {
    slug: 'tellraw-editor',
    title: 'Tellraw Editor',
    description: 'Build /tellraw messages with colors and click events.',
  },
  'banner-editor': {
    slug: 'banner-editor',
    title: 'Banner Editor',
    description: 'Design banners with pattern layers.',
  },
  'shield-editor': {
    slug: 'shield-editor',
    title: 'Shield Editor',
    description: 'Design shields using banner patterns.',
  },
  'potion-editor': {
    slug: 'potion-editor',
    title: 'Potion Editor',
    description: 'Custom potions, splash, lingering, and tipped arrows.',
  },
  'firework-editor': {
    slug: 'firework-editor',
    title: 'Firework Editor',
    description: 'Design fireworks with multiple explosions and colors.',
  },
  'armor-editor': {
    slug: 'armor-editor',
    title: 'Armor Editor',
    description: 'Dyed leather, trims, enchantments on armor sets.',
  },
  'book-editor': {
    slug: 'book-editor',
    title: 'Book Editor',
    description: 'Write books with formatting and click events.',
  },
  'armor-stand': {
    slug: 'armor-stand',
    title: 'Armor Stand Editor',
    description: 'Pose armor stands with 3D controls.',
  },
  'mob-builder': {
    slug: 'mob-builder',
    title: 'Mob Builder',
    description: 'Summon custom mobs with equipment, attributes and variants.',
  },
  'id-list': {
    slug: 'id-list',
    title: 'Minecraft ID List',
    description: 'Browse all items, blocks, entities, enchantments.',
  },
};
